import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteConfig } from '../../mocks/siteData';

const navLinks = [
  { label: 'Home', href: '/' },
  {
    label: 'Services',
    href: '/services',
    children: [
      { label: 'Auto Event Management', href: '/services/auto-event-management' },
      { label: 'Press & Model Launches', href: '/services/press-media-launches' },
      { label: 'Fleet Detailing & Maintenance', href: '/services/fleet-detailing-maintenance' },
      { label: 'Auto Show & Display Support', href: '/services/auto-show-support' },
      { label: 'Manufacturer Tours', href: '/services/manufacturer-tours' },
      { label: 'Golf & Sporting Events', href: '/services/golf-sporting-events' },
    ],
  },
  { label: 'About', href: '/about' },
  { label: 'Coverage', href: '/location/lake-forest' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#070707]/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="px-6 lg:px-12 xl:px-16">
        <div className="flex items-center justify-between h-[78px]">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="flex flex-col">
              <span className="font-condensed text-white text-xl font-bold tracking-widest leading-none uppercase">
                OTT <span className="text-gold">CAR CARE</span>
              </span>
              <span className="text-white/35 text-[9px] tracking-[0.3em] uppercase font-sans mt-0.5">
                Over The Top · Nationwide
              </span>
            </div>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setDropOpen(true)}
                  onMouseLeave={() => setDropOpen(false)}
                >
                  <button className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-sans font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    {link.label}
                    <div className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropOpen ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-gold/60 text-base" />
                    </div>
                  </button>
                  <div
                    className={`absolute top-full left-0 mt-3 w-60 bg-[#111] border border-gold/15 rounded-lg overflow-hidden transition-all duration-200 ${
                      dropOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="block px-5 py-3 text-[13px] font-sans text-white/60 hover:text-gold hover:bg-white/5 transition-all duration-150 border-b border-white/5 last:border-0 cursor-pointer whitespace-nowrap"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-white/70 hover:text-white text-sm font-sans font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href={siteConfig.phoneHref}
              className="flex items-center gap-2 text-gold/80 hover:text-gold text-sm font-sans font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-sm" />
              </div>
              {siteConfig.phone}
            </a>
            <Link
              to="/services"
              className="bg-gold hover:bg-gold-light text-[#0A0A0A] text-sm font-sans font-semibold px-6 py-2.5 rounded-none tracking-wide uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Request Support
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-[#070707]/98 border-t border-white/10 overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <div key={link.label}>
              <Link
                to={link.href}
                className="text-white/80 hover:text-white text-base font-sans font-medium block py-1 cursor-pointer"
              >
                {link.label}
              </Link>
              {link.children && (
                <div className="pl-4 mt-2 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="text-white/40 hover:text-gold text-sm font-sans block py-1 cursor-pointer"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
            <a href={siteConfig.phoneHref} className="flex items-center gap-2 text-gold font-sans text-sm cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill" /></div>
              {siteConfig.phone}
            </a>
            <Link
              to="/services"
              className="bg-gold text-[#0A0A0A] text-sm font-sans font-semibold px-5 py-3 text-center uppercase tracking-wide cursor-pointer whitespace-nowrap"
            >
              Request Event Support
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
